import "./index.css";
import "./App.css";

import { CredentialResponse, GoogleLogin, googleLogout } from "@react-oauth/google";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ClientLogger } from "./clientLogger";
import { ApiService, StatusResponse, TimeClock } from "./apiService";
import ReportsPage from "./Reports";
import UserAdminPage from "./UserAdminPage";

const logger = new ClientLogger("warn");
const apiService = new ApiService(logger);
const timeClock = new TimeClock(apiService);

function formatDuration(ms: number) {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return [h > 0 ? `${h}h` : null, m > 0 ? `${m}m` : null, `${s}s`].filter(Boolean).join(" ");
}

function formatDateString(str: string | undefined) {
    if (!str) return "";
    const dt = new Date(str);
    return dt.toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "short",
    });
}

type UserProps = {
    user: { name: string; email: string; token: string };
    status: StatusResponse;
    note: string;
    loading: boolean;
    setNote: (note: string) => void;
    onClockIn: () => void;
    onClockOut: () => void;
    onLogout: () => void;
    noteInputRef: React.RefObject<HTMLInputElement | null>;
};

function LoginView({ onLoginSuccess }: { onLoginSuccess: (res: CredentialResponse) => void }) {
    logger.logDebug("LoginView rendering");
    return (
        <button className="button-main" style={{ fontSize: "1.3rem" }}>
            <GoogleLogin onSuccess={onLoginSuccess} onError={() => alert("Login failed")} size="large" />
        </button>
    );
}

function UserControls({
    onLogout,
    status,
}: {
    onLogout: () => void;
    status?: StatusResponse;
}) {
    logger.logDebug("UserControls rendering");
    const navigate = useNavigate();
    return (
        <div className="menu">
            <button className="menu-link" onClick={onLogout} type="button">
                Log out
            </button>
            {status?.is_admin && (
                <button className="menu-link" onClick={() => navigate("/users")} type="button">
                    Users
                </button>
            )}
            <button className="menu-link" onClick={() => navigate("/reports")} type="button">
                Reports
            </button>
        </div>
    );
}

function UserClockedInView({
    status,
    note,
    setNote,
    loading,
    onClockOut,
    noteInputRef,
}: Pick<UserProps, "status" | "note" | "setNote" | "loading" | "onClockOut" | "noteInputRef">) {
    logger.logDebug("UserClockedInView rendering");
    return (
        <>
            <div className="support-text">
                <strong>Clocked in</strong> for {status.ms_on_clock ? formatDuration(status.ms_on_clock) : "..."}
            </div>
            <input
                className="note-input"
                ref={noteInputRef}
                placeholder="Optional: add a note (lunch, meeting...)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={loading}
                maxLength={150}
            />
            <button className="button-main" style={{ background: "#f34d4d" }} disabled={loading} onClick={onClockOut}>
                {loading ? "Processing..." : "Clock out"}
            </button>
        </>
    );
}

function UserClockedOutView({ status, loading, onClockIn }: Pick<UserProps, "status" | "loading" | "onClockIn">) {
    logger.logDebug("UserClockedOutView rendering");
    return (
        <>
            <div className="support-text">
                {status.clock_out ? (
                    <>
                        Last clocked in: <strong>{formatDateString(status.clock_out)}</strong>
                    </>
                ) : (
                    <>You are currently clocked out.</>
                )}
            </div>
            <button className="button-main" disabled={loading} onClick={onClockIn}>
                {loading ? "Processing..." : "Clock in"}
            </button>
        </>
    );
}

function MainView(props: UserProps) {
    logger.logDebug("MainView rendering");
    return (
        <>
            <UserControls onLogout={props.onLogout} status={props.status} />
            {props.status.status === "clocked_in" ? (
                <UserClockedInView
                    status={props.status}
                    note={props.note}
                    setNote={props.setNote}
                    loading={props.loading}
                    onClockOut={props.onClockOut}
                    noteInputRef={props.noteInputRef}
                />
            ) : (
                <UserClockedOutView status={props.status} loading={props.loading} onClockIn={props.onClockIn} />
            )}
        </>
    );
}

function ClockPage(props: UserProps) {
    logger.logDebug(`ClockPage props: ${props}`);

    if (!props.user || !props.status) {
        logger.logDebug("Redirecting to / because missing user/status");
        return null; // or loading spinner to avoid rendering anything until redirect fires
    }

    logger.logDebug("ClockPage rendering");
    return <MainView {...props} />;
}

function ReportsRoute({ user, status }: { user: any; status: StatusResponse }) {
    logger.logDebug(`ReportsRoute props: ${user}, ${status}`);
    const navigate = useNavigate();

    if (!user || !status) {
        return <Navigate to="/" replace />;
    }
    return (
        <ReportsPage apiService={apiService} user={user} isAdmin={status.is_admin} onBack={() => navigate("/clock")} />
    );
}

function UserAdminRoute({ user, status }: { user: any; status: StatusResponse }) {
    logger.logDebug(`UserAdminRoute props: ${user}, ${status}`);
    const navigate = useNavigate();

    if (!user || !status) {
        return <Navigate to="/" replace />;
    }
    return <UserAdminPage apiService={apiService} user={user} onBack={() => navigate("/clock")} />;
}

export default function App() {
    const [providerReady, setProviderReady] = useState(false);
    const [user, setUser] = useState<{ name: string; email: string; token: string } | null>(null);
    const [status, setStatus] = useState<StatusResponse | null>(null);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const noteInput = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        logger.logDebug("handleLogout");
        setUser(null);
        setStatus(null);
        localStorage.removeItem("user");
        googleLogout();
    }, []);

    useEffect(() => {
        logger.logDebug("useEffect, setProviderReady = true");
        setProviderReady(true);
    }, []);

    useEffect(() => {
        logger.logDebug("useEffect, get user from local storage if any");
        const saved = localStorage.getItem("user");
        if (saved) {
            logger.logDebug("useEffect, user found in local storage, setting user state");
            setUser(JSON.parse(saved));
        } else {
            logger.logDebug("useEffect, user not found in local storage");
        }
    }, []);

    useEffect(() => {
        if (!user) {
            logger.logDebug("useEffect, user state not set");
            setStatus(null);
            setNote("");
            setStatusLoading(false);
            return;
        }
        setStatusLoading(true);
        setLoading(true);
        apiService
            .getStatus(user.token)
            .then((status) => {
                switch (status.type) {
                    case "expired":
                        logger.logDebug("useEffect, user token expired, logging out");
                        handleLogout();
                        break;
                    case "ok":
                        logger.logDebug("useEffect, user token not expired, setting status state");
                        setStatus(status.data);
                        break;
                }
            })
            .catch(() => handleLogout())
            .finally(() => {
                setLoading(false);
                setStatusLoading(false);
            });
    }, [user, handleLogout]);

    async function handleLoginSuccess(credentialResponse: CredentialResponse) {
        logger.logDebug("handleLoginSuccess");
        const token = credentialResponse.credential ?? "";

        try {
            const response = await apiService.registerUser(token);

            if (response.type === "error") {
                // TODO: show error notification to user here
                logger.logError("Error from registerUser API");
                return;
            }

            const payload = JSON.parse(atob(token.split(".")[1]));
            const userData = {
                name: payload.name,
                email: payload.email,
                token,
            };

            logger.logDebug("handleLoginSuccess, user registered successfully");
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
            logger.logDebug("handleLoginSuccess, navigating to /clock");
            navigate("/clock");
        } catch (error) {
            logger.logError(`Error during user registration: ${error}`);
        }
    }

    async function handleClockIn() {
        logger.logDebug("handleClockIn");
        if (!user) {
            logger.logDebug("handleClockIn, user null");
            return;
        }
        setLoading(true);
        timeClock
            .clockIn(user.token)
            .then((status) => {
                setNote("");
                switch (status.type) {
                    case "expired":
                        logger.logDebug("handleClockIn, user token expired, logging out");
                        handleLogout();
                        break;
                    case "ok":
                        logger.logDebug("handleClockIn, user token not expired, setting status state");
                        setStatus(status.data);
                        break;
                    default:
                        logger.logError("Error clocking in");
                }
            })
            .finally(() => setLoading(false));
    }

    async function handleClockOut() {
        logger.logDebug("handleClockOut");
        if (!user) {
            logger.logDebug("handleClockOut, user null");
            return;
        }
        setLoading(true);
        timeClock
            .clockOut(user.token, note)
            .then((status) => {
                setNote("");
                switch (status.type) {
                    case "expired":
                        logger.logDebug("handleClockOut, user token expired, logging out");
                        handleLogout();
                        break;
                    case "ok":
                        logger.logDebug("handleClockOut, user token not expired, setting status state");
                        setStatus(status.data);
                        break;
                    default:
                        logger.logDebug("Error clocking out");
                }
            })
            .finally(() => setLoading(false));
    }

    const userProps: UserProps = {
        user: user as any,
        status: status as StatusResponse,
        note,
        setNote,
        loading,
        onClockIn: handleClockIn,
        onClockOut: handleClockOut,
        onLogout: handleLogout,
        noteInputRef: noteInput,
    };

    const location = useLocation();
    const isReportsPage = location.pathname === "/reports";

    return (
        <div className="main-wrapper">
            <div className="card" style={isReportsPage ? { maxWidth: 560, width: "100%" } : undefined}>
                <div className="brand-logo" style={isReportsPage ? { fontSize: "1.6rem" } : undefined}>
                    {isReportsPage ? "🕒 Reports" : "🕒 Timecard Pro"}
                </div>
                <Routes>
                    <Route
                        path="/"
                        element={
                            user ? (
                                <Navigate to="/clock" replace />
                            ) : providerReady ? (
                                <LoginView onLoginSuccess={handleLoginSuccess} />
                            ) : (
                                <div>Loading...</div> // or null if you prefer
                            )
                        }
                    />
                    <Route
                        path="/clock"
                        element={
                            user ? (
                                statusLoading ? (
                                    <div>Loading user status...</div>
                                ) : status ? (
                                    <ClockPage {...userProps} />
                                ) : (
                                    <div>Failed to load user status</div>
                                )
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            user ? (
                                statusLoading ? (
                                    <div>Loading user status...</div>
                                ) : status ? (
                                    <ReportsRoute user={user} status={status} />
                                ) : (
                                    <div>Failed to load user status</div>
                                )
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/users"
                        element={
                            user ? (
                                statusLoading ? (
                                    <div>Loading user status...</div>
                                ) : status ? (
                                    <UserAdminRoute user={user} status={status} />
                                ) : (
                                    <div>Failed to load user status</div>
                                )
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
        </div>
    );
}
